import { permissionsSelectors } from 'ducks/permissions'
import AvailableStocksButton from 'elements/hardwareSelectorV2/elements/AvailableStocksButton'
import { STOCK_LEVEL_SCENARIO, StockStatusMessagesType } from 'elements/hardwareSelectorV2/elements/stockUtils'
import {
  DistributorTradePricingContext,
  DistributorTradePricingContextType,
} from 'elements/hardwareSelectorV2/HardwareSelectorCore'
import IndicativePriceLabel from 'elements/IndicativePriceLabel'
import PriceOnApplicationPricingLabel from 'elements/PriceOnApplicationPricingLabel'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import { Skeleton, styled, Typography } from 'opensolar-ui'
import { useAddedToCartToast } from 'pages/ordering/addedToCartToast'
import ActionButton from 'pages/ordering/catalogv3/ActionButton'
import { STOCK_LEVEL_STATUS } from 'pages/ordering/constants'
import SupplierConnectionLink from 'pages/ordering/home/DistributorConnection/SupplierConnectionLink'
import useBulkOptions from 'pages/ordering/hooks/useBulkOptions'
import { useTranslate } from 'ra-core'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { OpenSolarPalette } from 'Themes'
import { makeOpenSolarStyles } from 'themes/makeOpenSolarStyles'
import { ActionType } from 'types/tracking'
import { BulkOptionType, ComponentLikeType, DistributorDataTypeV2 } from '../../../../elements/hardwareSelectorV2/types'
import TieredPriceOnApplicationPricingLabel from '../../../../elements/TieredPriceOnApplicationPricingLabel'
import { useGlobalCart } from '../../../../hooks/useGlobalCart'
import DistributorChips, { DistributorChipsLoading } from '../../catalogv3/DistributorsChip'
import { PricingText } from '../../catalogv3/ProductTile'
import useIsIndicativePricing from '../../hooks/useIsIndicativePricing'
import useIsPriceOnApplication from '../../hooks/useIsPriceOnApplication'
import useIsTieredPriceOnApplication from '../../hooks/useIsTieredPriceOnApplication'
import OrderLineItem from '../../OrderLineItem'
import BulkPricingText from './BulkPricingText'

// TODO these should be theme colors
const GREEN = '019939'
const RED = 'E12121'
const GREY = '4F4D55'
const BLUE = '113B98'

const useStyles = makeOpenSolarStyles((theme: any) => ({
  connectLink: {
    cursor: 'pointer',
    color: theme.straitPalette.blue[900],
    textDecoration: 'underline',
  },
  stockLevelTitle: {
    fontSize: 14,
  },
  stockLevelSubtitle: {
    marginTop: 8,
    fontSize: 12,
  },
  supplierSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 15,
  },
}))

const MainContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  backgroundColor: 'white',
  borderRadius: 8,
  height: '100%',
})

const ContentContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '16px',
  padding: '20px',
  borderBottom: `1px solid ${theme.palette.neutralGrey[600]}`,
}))

const HeaderContent = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: '16px',
  width: '100%',
}))

const ContentItem = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  width: '100%',
}))

const StockLevelWrapper = styled('div')(() => ({
  width: 'fit-content',
}))

const AvailabilityWrapper = styled('div')({
  minHeight: 16,
})

const ConnectLinkWrapper = styled('div')({
  minHeight: 27,
})

const Footer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 16,
  padding: '24px',
})

export const BulkMessage = styled('div')(({ theme }) => ({
  borderRadius: 4,
  padding: '4px 8px',
  backgroundColor: theme.palette.blue[200],
  color: theme.palette.blue[1000],
  fontSize: 12,
  height: 'fit-content',
  marginTop: 2,
  width: 'fit-content',
}))

const StockMessageWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: '100%',
  gap: 6,
  color: theme.palette.blue[900],
  textAlign: 'right',
}))

const MessageWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  justifyContent: 'flex-start',
  gap: 8,
}))

const PriceRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
})

const calculateCost = (quantity, price) => {
  const computedCost = quantity * price
  return computedCost
}

// TODO refactor this so the common logic with ProductTile is shared properly.
export const ProductDetailCheckoutMultiDistributor = ({
  component,
  currencySymbol,
  displayDetailsDistributor,
  selectedDistributor,
  showConnectDistributorLink,
  hasStocks,
  totalQuantityAvailable,
  stockLevelLabel,
  hasSelectedDistributor,
  setSelectedDistributor,
  onChangeDistributor,
  bulkOption,
  setBulkOption,
  isGlobalOutOfStock,
  showIsInStockElsewhere,
  discountPercentValue,
  dealPrice,
}: {
  component: ComponentLikeType | undefined
  currencySymbol: string
  displayDetailsDistributor: DistributorDataTypeV2 | undefined
  lowestPriceDistributor: DistributorDataTypeV2 | undefined
  selectedDistributor: DistributorDataTypeV2 | null
  showConnectDistributorLink: boolean
  hasStocks: boolean | number | undefined
  totalQuantityAvailable
  stockLevelLabel: StockStatusMessagesType | undefined
  hasSelectedDistributor: boolean
  setSelectedDistributor: (distributor: DistributorDataTypeV2 | null) => void
  onChangeDistributor: (distributor: DistributorDataTypeV2 | null, shouldShowDistributorPricing: boolean) => void
  bulkOption: BulkOptionType | null
  setBulkOption: (bulkOption: BulkOptionType | null) => void
  isGlobalOutOfStock: boolean
  showIsInStockElsewhere: boolean
  discountPercentValue?: number
  dealPrice?: number
}) => {
  const classes = useStyles()
  const translate = useTranslate()
  const [cartItem, setCartItem] = useState<OrderLineItem | null>(null)
  const [computedCost, setComputedCost] = useState<number>()
  const [bulkQuantity, setBulkQuantity] = useState<number>(1)
  const { trackUserInteraction } = useTrackComponent({
    componentKey: 'product_detail_checkout_multi_distributor',
    eventName: 'Hardware Product',
  })
  const { addLineItemFromV2Component, updateLineItemQty, removeLineItem, orderableLineItems } = useGlobalCart()
  const { showAddedToCartToast } = useAddedToCartToast()
  const distributorTradePricing =
    useContext<DistributorTradePricingContextType>(DistributorTradePricingContext).distributorTradePricing
  const { allowView: allowViewPricing } = useSelector((state) =>
    permissionsSelectors.getPermissionByKey(state, 'component_pricing_costing')
  )
  const {
    isBulkAvailable,
    lowestBulkPrice,
    bulkPriceDistributorNames,
    selectedDistributorLowestBulkPrice,
    bulkOptions,
    bulkPriceMessage,
  } = useBulkOptions({
    component,
    selectedDistributor: displayDetailsDistributor,
  })
  useEffect(() => {
    const cartOrderItem = orderableLineItems.find(
      (lineItem: OrderLineItem) =>
        lineItem.code === component?.code && lineItem.selectedDistributor === displayDetailsDistributor?.distributor
    )
    if (!cartOrderItem) {
      setCartItem(null)
      return
    }
    setCartItem(cartOrderItem)
  }, [orderableLineItems, component?.code, displayDetailsDistributor])

  const defaultStockMessage = useMemo(() => {
    if (totalQuantityAvailable === 0) {
      return 'Out of stock'
    }
    return 'In Stock'
  }, [totalQuantityAvailable])

  useEffect(() => {
    if (bulkOption) {
      setComputedCost(calculateCost(bulkQuantity > 0 ? bulkQuantity : 1, bulkOption.price_per_unit))
      return
    }
    const item_price = displayDetailsDistributor ? displayDetailsDistributor?.price_detail?.price_ex_vat : 0
    if (cartItem && displayDetailsDistributor?.price_detail?.price_ex_vat) {
      setComputedCost(calculateCost(cartItem.quantity, item_price?.toFixed(2)))
    } else {
      setComputedCost(
        calculateCost(!!bulkOptions.length && bulkQuantity > 0 ? bulkQuantity : 1, item_price?.toFixed(2))
      )
    }
  }, [cartItem?.quantity, displayDetailsDistributor, bulkOption, bulkQuantity])

  useEffect(() => {
    // Do this in an effect because we want the current value of `shouldShowDistributorPricing` to be used.
    onChangeDistributor(selectedDistributor, !showConnectDistributorLink)
  }, [selectedDistributor, showConnectDistributorLink])

  useEffect(() => {
    if (component?.ordering_v2.length === 1) {
      setSelectedDistributor(component?.ordering_v2[0])
    }
  }, [component])

  // There are a few different states we need to handle here depending on the stock level and distributor.
  const titleColour = useMemo(() => {
    // TODO: these titleColors share a lot with ProductTile.
    if (selectedDistributor?.distributor === 'outlet') {
      if (
        [STOCK_LEVEL_STATUS.AVAILABLE_FOR_PRE_ORDER, STOCK_LEVEL_STATUS.AVAILABLE].includes(
          stockLevelLabel?.status || ''
        )
      ) {
        return GREEN
      } else {
        return RED
      }
    }
    if (showConnectDistributorLink) {
      return GREY
    } else if (
      stockLevelLabel?.scenario &&
      [
        STOCK_LEVEL_SCENARIO.OUT_OF_STOCK,
        STOCK_LEVEL_SCENARIO.PARTIALLY_IN_STOCK,
        STOCK_LEVEL_SCENARIO.OUT_OF_STOCK_WITH_INCOMING_SCHEDULE,
      ].includes(stockLevelLabel?.scenario)
    ) {
      return RED
    } else if (!selectedDistributor) {
      return GREEN
    }
    return totalQuantityAvailable && Number(totalQuantityAvailable) > 0 ? GREEN : RED
  }, [selectedDistributor, stockLevelLabel?.status, showConnectDistributorLink, totalQuantityAvailable])

  const subtitleColour = useMemo(() => {
    if (stockLevelLabel?.scenario === STOCK_LEVEL_SCENARIO.OUT_OF_STOCK_WITH_INCOMING_SCHEDULE) {
      return GREY
    }

    return GREY
  }, [stockLevelLabel?.scenario])

  const isIndicativePricing = useIsIndicativePricing((displayDetailsDistributor?.distributor as any) || undefined)
  const isPriceOnApplication = useIsPriceOnApplication((displayDetailsDistributor?.distributor as any) || undefined)
  const isTieredPriceOnApplication = useIsTieredPriceOnApplication(
    (displayDetailsDistributor?.distributor as any) || undefined,
    displayDetailsDistributor?.price
  )

  const handleQuantityChange = (quantity: number) => {
    if (!component) return

    if (quantity === 0 || !quantity) {
      trackUserInteraction(
        { type: ActionType.CLICK },
        { action: 'removed', source: 'quantity_field', context: 'product_details' }
      )
      removeLineItem(cartItem?.uuid)
      setCartItem(null)
      return
    }
    // Update the quantity in the cart
    if (!cartItem?.uuid) return
    updateLineItemQty(cartItem.uuid, quantity)
    // Refresh the toast so it shows the updated quantity (same as product tile)
    if (displayDetailsDistributor) {
      const unitPrice = displayDetailsDistributor.price_detail?.price_ex_vat ?? cartItem?.pricePerUnit ?? 0
      const imageUrl =
        displayDetailsDistributor?.image_url ||
        component.ordering_v2?.find((o: { image_url?: string }) => o.image_url)?.image_url
      showAddedToCartToast({
        variant: 'item',
        item: {
          description: displayDetailsDistributor?.short_description ?? component.name,
          code: component.code,
          imageUrl,
          quantity,
          price: dealPrice ?? unitPrice,
          distributor: displayDetailsDistributor?.distributor,
        },
      })
    }
  }

  const handleAddToCart = (quantity?: number, bulkOption?: BulkOptionType | null) => {
    if (!component) return
    if (!displayDetailsDistributor || Object.keys(displayDetailsDistributor).length === 0) {
      return
    }
    trackUserInteraction(
      { type: ActionType.CLICK },
      { action: 'clicked', source: 'add_to_cart_button', context: 'product_details' }
    )
    const qty = quantity ?? bulkQuantity ?? 1
    const unitPrice = displayDetailsDistributor.price_detail?.price_ex_vat ?? 0
    addLineItemFromV2Component(component, displayDetailsDistributor, quantity, bulkOption)

    // If we add the item without having selected a distributor, we should set the distributor
    // to be identical to displayDetailsDistributor.
    if (!selectedDistributor) {
      setSelectedDistributor(displayDetailsDistributor)
    }

    const imageUrl = displayDetailsDistributor?.image_url || component.ordering_v2?.find((o) => o.image_url)?.image_url
    showAddedToCartToast({
      variant: 'item',
      item: {
        description: displayDetailsDistributor?.short_description ?? component.name,
        code: component.code,
        imageUrl,
        quantity: qty,
        price: dealPrice ?? unitPrice,
        distributor: displayDetailsDistributor?.distributor,
      },
    })
  }

  const handleDistributorSelect = (e, distributor) => {
    if (!component) return
    // Reset the bulk option and quantity when the distributor is changed
    setBulkOption(null)
    setBulkQuantity(1)
    if (selectedDistributor === distributor && component.ordering_v2.length > 1) {
      setSelectedDistributor(null)
    } else {
      setSelectedDistributor(distributor)
    }
  }

  return (
    <MainContainer>
      <ContentContainer>
        <HeaderContent>
          <StockLevelWrapper>
            <ContentItem>
              {!component ? (
                <Skeleton width={55} height={16} />
              ) : (
                <Typography textVariant="subtitle1" colorHex={`#${titleColour}`}>
                  {selectedDistributor
                    ? translate(stockLevelLabel?.productDetailsStockMessage?.title || '')
                    : defaultStockMessage}
                </Typography>
              )}
              {!component ? (
                <AvailabilityWrapper>
                  <Skeleton width={70} height={16} />
                </AvailabilityWrapper>
              ) : (
                stockLevelLabel?.productDetailsStockMessage?.subtitle && (
                  <AvailabilityWrapper>
                    <div className={classes.stockLevelSubtitle} style={{ color: `#${subtitleColour}` }}>
                      {stockLevelLabel?.productDetailsStockMessage?.subtitle}
                    </div>
                  </AvailabilityWrapper>
                )
              )}
            </ContentItem>
          </StockLevelWrapper>
          <MessageWrapper>
            {isBulkAvailable && allowViewPricing && <BulkMessage>{bulkPriceMessage}</BulkMessage>}
            {!isGlobalOutOfStock && (
              <StockMessageWrapper>
                <AvailableStocksButton
                  data={component?.ordering_v2 ?? []}
                  selectedDistributor={selectedDistributor?.distributor}
                  inStockElsewhere={showIsInStockElsewhere}
                  productCode={component?.code}
                  productName={displayDetailsDistributor?.short_description ?? component?.name}
                  amplitudeEventType="Hardware Product"
                />
              </StockMessageWrapper>
            )}
          </MessageWrapper>
        </HeaderContent>
        <ContentItem>
          {!component ? (
            <>
              <Skeleton width={140} height={39} />
            </>
          ) : isTieredPriceOnApplication ? (
            <TieredPriceOnApplicationPricingLabel
              supplierFilterKey={(displayDetailsDistributor?.distributor as any) || undefined}
            />
          ) : isPriceOnApplication ? (
            <PriceOnApplicationPricingLabel />
          ) : (
            displayDetailsDistributor?.price &&
            allowViewPricing && (
              <PriceRow>
                <PricingText
                  selectedDistributor={selectedDistributor}
                  displayDetailsDistributor={displayDetailsDistributor}
                  totalCost={dealPrice ?? computedCost}
                  currencySymbol={currencySymbol}
                  showPricingForSelectedDistributor={selectedDistributor !== null && !showConnectDistributorLink}
                  isIndicativePricing={isIndicativePricing}
                  discountPercentValue={discountPercentValue}
                  dealPrice={dealPrice ?? displayDetailsDistributor?.price ?? 0}
                  hideSubtext
                />
                {isIndicativePricing && <IndicativePriceLabel />}
              </PriceRow>
            )
          )}
        </ContentItem>
        {isBulkAvailable && allowViewPricing && (
          <ContentItem>
            <BulkPricingText
              lowestPrice={lowestBulkPrice}
              distributorNames={bulkPriceDistributorNames}
              currencySymbol={currencySymbol}
              selectedDistributorLowestBulkPrice={selectedDistributorLowestBulkPrice}
              bulkOption={bulkOption}
              isDistributorSelected={selectedDistributor !== null}
            />
          </ContentItem>
        )}

        <ContentItem>
          <div className={classes.supplierSection}>
            <Typography textVariant="helper1" colorHex={OpenSolarPalette.greyMid1}>
              {`${translate('SELECT SUPPLIER')}`}
            </Typography>
            {component ? (
              <DistributorChips
                ordering_v2={component.ordering_v2}
                onSelect={handleDistributorSelect}
                selectedDistributor={selectedDistributor}
                location="product-details"
              />
            ) : (
              <DistributorChipsLoading />
            )}
          </div>
        </ContentItem>
        {showConnectDistributorLink && (
          <ContentItem>
            <ConnectLinkWrapper>
              <SupplierConnectionLink
                showConnectDistributorLink={showConnectDistributorLink}
                selectedSupplier={selectedDistributor?.distributor}
              />
            </ConnectLinkWrapper>
          </ContentItem>
        )}
      </ContentContainer>
      <Footer>
        <ActionButton
          cartItem={cartItem}
          showConnectDistributorLink={showConnectDistributorLink}
          handleAddToCart={handleAddToCart}
          handleQuantityChange={handleQuantityChange}
          bulkOptions={bulkOptions}
          selectedBulkOption={bulkOption}
          setBulkOption={setBulkOption}
          bulkQuantity={bulkQuantity}
          setBulkQuantity={(quantity) => setBulkQuantity(quantity)}
          isDisabled={isGlobalOutOfStock}
        />
        <Typography textVariant="helper1" colorHex={OpenSolarPalette.greyMid1}>
          {translate('Shipping options will be available during checkout.')}
        </Typography>
      </Footer>
    </MainContainer>
  )
}
